import { useMutation } from '@tanstack/react-query';
import useErrorBanner from 'hooks/error-banner/useErrorBanner';
import useEcoms from 'hooks/useEcoms';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ShippingZoneService from 'services/settings/ppoints/ShippingZoneService';

/**
 * Creates a Shopify shipping zone for a single country, mirroring the missing-zone
 * banner action on the shipping zones settings page.
 */
const useCreateShippingZone = (onZoneCreated?: () => Promise<unknown> | void) => {
    const ecoms = useEcoms();
    const { t } = useTranslation();
    const { setBanner, emptyBanner } = useErrorBanner();

    const createZoneMutation = useMutation({
        mutationKey: ['create-zone'],
        mutationFn: (countryName: string) => ShippingZoneService.createZone(ecoms.id, '', [countryName]),
    });

    const createZone = useCallback(
        async (countryName: string) => {
            if (!ecoms.id || !countryName) return;

            emptyBanner();

            try {
                await createZoneMutation.mutateAsync(countryName);
                await onZoneCreated?.();
                shopify.toast.show(t('SHIPPING_ZONES.TOASTS.ZONE_CREATED'));
            } catch {
                setBanner({
                    titleKey: t('SHIPPING_ZONES.ERROR_CREATE_NEW_ZONE.TITLE'),
                    descriptionKey: t('SHIPPING_ZONES.ERROR_CREATE_NEW_ZONE.DESCRIPTION'),
                    withSupportActions: true,
                    tone: 'warning',
                });
            }
        },
        [ecoms.id, createZoneMutation.mutateAsync, onZoneCreated, emptyBanner, setBanner, t]
    );

    return {
        createZone,
        isCreatingZone: createZoneMutation.isLoading,
        /** The country a zone is currently being created for, if any. */
        creatingCountry: createZoneMutation.isLoading ? createZoneMutation.variables : undefined,
    };
};

export default useCreateShippingZone;
